import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import CustomModal from '../components/modal'
import ContactForm from './contact'
import HeroSection from '../components/HeroSection'
import ServicesSection from '../components/ServicesSection'
import SectorSection from '../components/SectorSection'
import SoftwareSection from '../components/SoftwareSection'
import PartnersSection from '../components/PartnersSection'
import OpenSourceSection from '../components/OpenSourceSection'
import MS365AlternativeSection from '../components/MS365AlternativeSection'
import HostingSection from '../components/HostingSection'
import CTASection from '../components/CTASection'
import WhyPathConnectSection from '../components/WhyPathConnectSection'
// Footer is rendered elsewhere
import Background from '../components/layout/background'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowUp } from '@fortawesome/free-solid-svg-icons'

/**
 * Enhanced Home component with scroll animations and improved sections
 * Integrates with the existing Background component and keeps all original sections
 */
const Home = () => {
  // State management
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [showScrollTop, setShowScrollTop] = useState(false)

  // Add effect to handle scroll animations
  useEffect(() => {
    // Intersection Observer for section animations
    const sectionObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('section-visible');
        }
      });
    }, { threshold: 0.15 });
    
    // Observe all sections for animation
    document.querySelectorAll('.animate-section').forEach(section => {
      sectionObserver.observe(section);
    });
    
    // Handle scroll for showing back-to-top button
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > window.innerHeight);
    };
    
    window.addEventListener('scroll', handleScroll);
    
    // Call once to initialize
    handleScroll();
    
    // Cleanup function when component unmounts
    return () => {
      window.removeEventListener('scroll', handleScroll);
      sectionObserver.disconnect();
    };
  }, []);

  const { t } = useTranslation('home')
  
  // Scroll to top function
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };
  
  return (
    <Background>
      {/* Modal for contact form */}
      <CustomModal
        isOpen={isModalOpen}
        onRequestClose={() => setIsModalOpen(false)}
      >
        <ContactForm />
      </CustomModal>
      
      {/* Back to top button */}
      <button 
        onClick={scrollToTop}
        className={`fixed bottom-8 right-8 bg-primary/90 hover:bg-primary text-white rounded-full p-3 shadow-lg z-50 transition-all duration-500 ${showScrollTop ? 'opacity-100 scale-100' : 'opacity-0 scale-75 pointer-events-none'}`}
        aria-label="Back to top"
      >
        <FontAwesomeIcon icon={faArrowUp} className="text-lg" />
      </button>

      {/* Hero Section */}
      <section className="animate-section">
        <HeroSection />
      </section>
      
      {/* Services Section */}
      <section className="animate-section mt-4">
        <ServicesSection openContactModal={() => setIsModalOpen(true)} />
      </section>
      
      {/* MS365 Alternative Section */}
      <section className="animate-section mt-16 lg:mt-24">
        <MS365AlternativeSection />
      </section>
      
      {/* Partners Section */}
      <section className="animate-section mt-16 lg:mt-24">
        <PartnersSection />
      </section>
      
      {/* Hosting Section - Now using the separate component */}
      <section className="animate-section mt-16 lg:mt-24">
        <HostingSection />
      </section>
      
      {/* Sector Section */}
      <section className="animate-section mt-20 lg:mt-32">
        <SectorSection />
      </section>
      
      {/* Software Section */}
      <section className="animate-section mt-20 lg:mt-32">
        <SoftwareSection />
      </section>
      
      {/* Open Source section */}
      <section className="animate-section mt-20 lg:mt-32">
        <OpenSourceSection />
      </section>
      
      {/* New CTA Section */}
      <section className="animate-section mt-20 lg:mt-32">
        <CTASection openContactModal={() => setIsModalOpen(true)} />
      </section>
      
      {/* Why PathConnect Section */}
      <section className="animate-section mt-16 lg:mt-24">
        <WhyPathConnectSection />
      </section>
      
      {/* Footer is already included in the Background component */}
    </Background>
  )
}

export default Home